import { Card, CardContent } from '@/components/ui/card';
import { Users, Briefcase, Building2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTypingAnimation } from '@/hooks/use-typing-animation';
import { useSiteContent } from '@/hooks/use-site-content';

const AboutSection = () => {
  const { getText, getJson } = useSiteContent('about');

  const sectionSubtitle = getText('section_subtitle', 'My Journey');
  const sectionHeading = getText('section_heading', 'Key Achievements');
  const aboutSubtitle = getText('about_subtitle', 'About Rakib Hossen Topu');
  const aboutHeading = getText('about_heading', 'Digital Marketing');
  const aboutParagraph1 = getText('about_paragraph_1', "I'm Rakib Hossen Topu, a professional SEO Expert in BD and one of the best SEO experts in Bangladesh with over 5 years of experience helping businesses grow online.");
  const aboutParagraph2 = getText('about_paragraph_2', 'As one of the top SEO experts in Bangladesh, each project showcases my dedication to detail, creativity, and excellence.');
  const statsData = getJson<{ stats: { value: string; label: string }[] }>('stats', {
    stats: [
      { value: '10+', label: 'Projects Completed' },
      { value: '5+', label: 'Companies Worked With' },
      { value: '10+', label: 'Happy Clients' },
    ]
  });

  const { ref: heading1Ref, displayedText: heading1Text } = useTypingAnimation(sectionHeading, 60);
  const { ref: heading2Ref, displayedText: heading2Text } = useTypingAnimation(aboutHeading, 60);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      {
        threshold: 0.3,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !contentVisible) {
          setContentVisible(true);
        }
      },
      {
        threshold: 0.2,
      }
    );

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    return () => observer.disconnect();
  }, [contentVisible]);

  const iconMap = [Briefcase, Building2, Users];

  const stats = statsData.stats.map((s, i) => ({
    icon: iconMap[i % iconMap.length],
    value: s.value,
    label: s.label,
  }));
  return (
    <section id="about" className="py-24 bg-gradient-to-b from-background to-secondary/30 overflow-hidden" ref={sectionRef} aria-label="About Rakib Hossen Topu - SEO Expert in Bangladesh">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Stats Cards */}
            <aside 
              className={`space-y-6 transition-all duration-700 ${
                hasAnimated 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-12'
              }`}
              aria-label="Key Achievements"
            >
              <div className="space-y-2 mb-8">
                <p className={`text-primary font-semibold uppercase tracking-wider text-sm transition-all duration-500 ${
                  hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`} style={{ transitionDelay: '100ms' }}>
                  {sectionSubtitle}
                </p>
                <h3 ref={heading1Ref as any} className="text-3xl font-bold min-h-[2.5rem]">
                  {heading1Text}
                </h3>
              </div>
              <div className="grid gap-5">
                {stats.map((stat, index) => (
                  <StatCard key={index} stat={stat} index={index} hasAnimated={hasAnimated} />
                ))}
              </div>
            </aside>

            {/* About Content */}
            <article 
              ref={contentRef}
              className={`space-y-8 transition-all duration-700 ${
                contentVisible 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-12'
              }`}
              style={{ transitionDelay: '200ms' }}
              itemScope 
              itemType="https://schema.org/Person"
            >
              <div className="space-y-4">
                <p className={`text-primary font-semibold uppercase tracking-wider text-sm transition-all duration-500 ${
                  contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`} style={{ transitionDelay: '300ms' }}>
                  {aboutSubtitle}
                </p>
                <h2 ref={heading2Ref as any} className="text-4xl md:text-5xl font-bold leading-tight min-h-[3.5rem]">
                  {heading2Text} <span className="gradient-text">Expert</span>
                </h2>
                <meta itemProp="name" content="Rakib Hossen Topu" />
                <meta itemProp="jobTitle" content="SEO Expert in BD | Digital Marketing Specialist" />
                <meta itemProp="email" content="rakibhassantopu@gmail.com" />
                <meta itemProp="url" content="https://rakibhossentopu.online" />
                <p className={`text-muted-foreground text-lg leading-relaxed transition-all duration-500 ${
                  contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`} style={{ transitionDelay: '400ms' }} itemProp="description"
                  dangerouslySetInnerHTML={{ __html: aboutParagraph1 }}
                />
                <p className={`text-muted-foreground leading-relaxed transition-all duration-500 ${
                  contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`} style={{ transitionDelay: '500ms' }}
                  dangerouslySetInnerHTML={{ __html: aboutParagraph2 }}
                />
                <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress" className="hidden">
                  <meta itemProp="addressLocality" content="Dhaka" />
                  <meta itemProp="addressCountry" content="Bangladesh" />
                </div>
                <div className="hidden">
                  <span itemProp="knowsAbout">SEO Expert</span>
                  <span itemProp="knowsAbout">SEO Expert in BD</span>
                  <span itemProp="knowsAbout">Digital Marketing</span>
                  <span itemProp="knowsAbout">Local SEO</span>
                  <span itemProp="knowsAbout">E-commerce SEO</span>
                  <span itemProp="knowsAbout">YouTube SEO</span>
                  <span itemProp="knowsAbout">Technical SEO</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatCard = ({
  stat,
  index,
  hasAnimated,
}: {
  stat: {
    icon: any;
    value: string;
    label: string;
  };
  index: number;
  hasAnimated: boolean;
}) => {
  const [count, setCount] = useState(0);
  const targetNumber = parseInt(stat.value);
  const suffix = stat.value.replace(/[0-9]/g, '');

  useEffect(() => {
    if (!hasAnimated) return;

    let startTime: number;
    const duration = 2000;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * targetNumber));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [hasAnimated, targetNumber]);

  const Icon = stat.icon;
  
  return (
    <Card 
      className={`p-6 card-hover bg-card border-border/50 backdrop-blur-sm transition-all duration-500 hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/10 hover:border-primary/30 ${
        hasAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{
        transitionDelay: `${index * 150}ms`
      }}
    >
      <CardContent className="p-0 flex items-center gap-5">
        <div className={`p-4 rounded-2xl bg-gradient-to-br from-primary/10 to-primary-glow/5 border border-primary/20 transition-all duration-300 group-hover:scale-110 ${
          hasAnimated ? 'animate-pulse' : ''
        }`} style={{ animationDuration: '3s', animationDelay: `${index * 200}ms` }}>
          <Icon className="w-8 h-8 text-primary" />
        </div>
        <div>
          <div className="text-4xl font-bold gradient-text mb-1">
            {count}{suffix}
          </div>
          <p className="text-muted-foreground text-sm font-medium">{stat.label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AboutSection;
